import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { DynamicForm, DynamicFormField, FieldOption } from '../../../interfaces/dynamic-form.interface';
import { DynamicFormService } from '../../../services/dynamic-form.service';
import { DynamicFormRendererComponent } from '../dynamic-form-renderer/dynamic-form-renderer.component';
import { ButtonComponent } from '@ppl/ui';
import { ChecklistGroup, ChecklistItem } from 'src/app/interfaces/checklist.model';
import { CheckListService } from 'src/app/services/check-list.service';
import { PDFFormConfig } from 'src/app/interfaces/config';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzAlertModule } from 'ng-zorro-antd/alert';
import { StorageService } from 'src/app/services/storage.service';
import { Subject, takeUntil } from 'rxjs';
import { ErrorAlertService } from 'src/app/shared/services/error-alert.service';
import { IC_PROVIDER_CLASSIFICATION_FORM, VE_PROVIDER_CLASSIFICATION_FORM } from 'src/app/shared/constants/static-data.constants';
import { LeadDetailsService } from 'src/app/services/lead-details.service';
import {
  STATIC_DATA,
  TAX_ID_TYPE,
  TaxIdType,
  W9_FIELD_IDS,
  W9_FORM_NAME,
  W9_UNMASK_IDENTIFIER_CONTEXT,
} from 'src/app/shared/constants/static-data.constants';

@Component({
  selector: 'ppl-provider-enrollment-forms',
  standalone: true,
  imports: [CommonModule, DynamicFormRendererComponent, ButtonComponent, NzFormModule, NzAlertModule],
  templateUrl: './provider-enrollment-forms.component.html',
  styleUrls: ['./provider-enrollment-forms.component.css']
})
export class ProviderEnrollmentFormsComponent implements OnInit, OnChanges, OnDestroy {
  // region --- Inputs / Outputs ---
  @Input() providerId!: string;
  @Input() selectedProviderType?: string;
  @Input() selectedParticipantId!: number;
  @Input() participantPPLID!: string;
  @Input() programCode!: string;
  @Input() source: string = '';
  @Input() readonly: boolean = false;
  @Input() checklistGroups?: ChecklistGroup[] = [];
  @Input() isAssociation: boolean = false;
  @Input() relationToParticipant: string = '';
  // Employer ⇄ Provider forms: explicit prtcpntPrvdrIdAndTypes ([{IR, prvPplId},{EMP, empPplId}]) that
  // overrides the default participant PTC remap when loading form data. Undefined for normal flows.
  @Input() formEntitiesOverride?: { userType: string; primaryId: string }[];
  // Employer ⇄ Provider linked forms: routes dynamic-form-renderer save/sign to the new endpoints.
  @Input() linkedSaveConfig?: { mode: 'employerProvider' | 'providerEmployer'; employerId: string; providerId: string; userType: string };
  // Basic info / contact details / identifiers from EnrollmentDataService.getAssociationsDetails(),
  // used to prefill the W-9's Legal Name of Business, DBA, Mailing Address and SSN/EIN fields.
  @Input() associationDetails?: any;

  @Output() allFormsCompleted = new EventEmitter<boolean>();
  @Output() formsLoaded = new EventEmitter<number>();
  @Output() actions = new EventEmitter<any>();
  // endregion

  // region --- View children ---
  private _formRenderer?: DynamicFormRendererComponent;

  @ViewChild(DynamicFormRendererComponent)
  set formRenderer(component: DynamicFormRendererComponent | undefined) {
    this._formRenderer = component;
    this.applyPrefillIfCached();
  }

  get formRenderer(): DynamicFormRendererComponent | undefined {
    return this._formRenderer;
  }
  // endregion

  // region --- Component state ---
  forms: DynamicForm[] = [];
  currentChecklist: ChecklistItem | null = null;
  currentChecklistGroup: ChecklistGroup | null = null;
  /** When a refetch was requested, remember which form was selected so we can restore it after
   *  the new checklistGroups arrive. Matches by formMasterId because checklistItemId can change. */
  private pendingRestoreFormMasterId: number | null = null;
  loading = true;
  error = false;
  errorMessage = '';
  disableActions = false;
  disableSave = false;
  pdfConfig?: PDFFormConfig;
  pdfFile?: string;

  /** Service Agreement form master ids: IC (164), Vendor (165) and IF(166). */
  private readonly SERVICE_AGREEMENT_FORM_IDS = [164, 165, 166];

  /**
   * The Service Agreement questionnaire is the provider's to fill in — the participant only ever
   * reviews it, so it is read-only on the participant side regardless of the tab's own readonly.
   */
  get isReadOnly(): boolean {
    if (this.readonly) {
      return true;
    }
    return this.source === 'participant'
      && this.SERVICE_AGREEMENT_FORM_IDS.includes(Number(this.currentChecklist?.formMasterId));
  }

  private prefillCache = new Map<string, Record<string, any>>();
  private currentFormSaved = false;
  private destroy$ = new Subject<void>();

  private readonly FLSA_FORM_ID = 83;
  private readonly TAX_EXEMPTIONS_FORM_ID = 81;
  private readonly I9_FORM_ID = 109;
  // Tax Exemptions "Additional Information" multi-checkbox field + the non-resident-alien visa option.
  private readonly TAX_ADDITIONAL_INFO_FIELD = 'Part 2';
  private readonly NON_RESIDENT_ALIEN_VALUE = 'Non-resident Alien';
  // I-9 citizenship question + the value that indicates a US citizen.
  private readonly I9_CITIZENSHIP_FIELD = 'Citizenship or immigration status';
  private readonly I9_US_CITIZEN_VALUE = 'Citizen';
  // Latest I-9 citizenship answer, read from the I-9 form's saved data.
  private i9CitizenshipStatus?: string;
  // endregion

  constructor(
    private dynamicFormService: DynamicFormService,
    private checkListService: CheckListService,
    private storageService: StorageService,
    private errorAlertService: ErrorAlertService,
    private leadDetailsService: LeadDetailsService,
  ) { }

  // ───────────────────────────────────────────────
  //  Lifecycle
  // ───────────────────────────────────────────────

  ngOnInit(): void {
    if (this.selectedProviderType == "EMP") {
      this.readonly = true;
    }

    this.loadForms(false);

    this.storageService.hasEINAvailableForEmployer$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.loadForms(true));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.selectedProviderType == "EMP") {
      this.readonly = true;
    }

    if (!this.shouldReloadForms(changes)) {
      return;
    }

    this.loadForms(true);
  }

  // ───────────────────────────────────────────────
  //  Navigation
  // ───────────────────────────────────────────────

  navigateToChecklistByItem(item: ChecklistItem): void {
    // Scenarios 1 & 2: the Tax Exemptions form is gated behind a completed I-9.
    if (item.formMasterId === this.TAX_EXEMPTIONS_FORM_ID && !this.isI9Complete()) {
      this.errorAlertService.openModal('error', 'I-9 Required',
        'Please fully complete your I-9 form before accessing the Tax Exemption form.');
      return;
    }

    this.currentChecklist = item;
    this.currentChecklistGroup = this.checklistGroups?.find(g => g.items.includes(item)) ?? null;
    this.currentFormSaved = false;
    // Re-evaluate the non-resident-alien option grey-out against the latest I-9 citizenship answer.
    this.applyTaxExemptionFieldRules();
    this.loadFormData(this.currentChecklist);
    this.applyPrefillIfCached();
  }

  // ───────────────────────────────────────────────
  //  Form actions & event handlers
  // ───────────────────────────────────────────────

  onAction(event: any): void {
    // Before opening the PDF sign dialog, ensure all required dynamic-form fields are filled.
    // Skip validation for already-completed forms (View Form).
    if (event === 'edit' && this.readonly && this.selectedProviderType === 'EMP') {
      this.actions.emit({ action: event, data: this.currentChecklist });
      return;
    }

    // Same for a read-only questionnaire: every control is disabled so formGroup.valid is false,
    // and the completeness gate below would silently swallow the click. The gate exists to stop
    // whoever fills the form signing it half-finished — it does not apply to a viewer who is only
    // signing what someone else completed.
    if (event === 'edit' && this.isReadOnly) {
      this.actions.emit({ action: event, data: this.currentChecklist });
      return;
    }

    const isCompleted = this.currentChecklist?.formStatus === 'C';
    if (event === 'edit' && !isCompleted && this.hasQuestionnaireFields()) {
      if (!this.formRenderer) return;

      const { formGroup } = this.formRenderer;
      if (!formGroup.valid) {
        formGroup.markAllAsTouched();
        this.scrollToFirstError();
        return;
      }
    }
    this.actions.emit({ action: event, data: this.currentChecklist });
  }

  /** Scrolls to the first validation error so the user can see what's wrong. */
  private scrollToFirstError(): void {
    setTimeout(() => {
      const errorEl = document.querySelector('.dynamic-form .error-message');
      errorEl?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }

  onSave(): void {
    // The demographics update triggers a backend process on completion, so it must fully finish
    // (success or failure) before the questionnaire's own onSave fires — these are async calls
    // to different endpoints and the ordering isn't guaranteed otherwise.
    this.updateLegalNameAndBusinessNameIfChanged(() => {
      this.ensureSelectedTaxIdSegmentsPopulated(() => {
        this.formRenderer?.onSave(this.isAssociation);
      });
    });
  }

  // Real (unmasked) SSN/EIN digits already fetched this session, keyed by "{providerId}:{type}"
  // — the unmask API is only ever called once per type per provider.
  private unmaskedIdentifierCache = new Map<string, string>();

  private static readonly TIN_SEGMENT_FIELD_IDS: Record<TaxIdType, string[]> = {
    [TAX_ID_TYPE.SSN]: [W9_FIELD_IDS.SSN_SEGMENT_1, W9_FIELD_IDS.SSN_SEGMENT_2, W9_FIELD_IDS.SSN_SEGMENT_3],
    [TAX_ID_TYPE.EIN]: [W9_FIELD_IDS.EIN_SEGMENT_1, W9_FIELD_IDS.EIN_SEGMENT_2],
  };

  /**
   * W-9 only (formMasterId 107/123/124 — see isW9Form): once a Taxpayer ID type's real PDF
   * segments have been blanked by a prior save (the other type was selected then — see
   * dynamic-form-renderer's transformFormValueToLabelBased), switching back to that type would
   * otherwise re-save it as blank forever, since nothing else repopulates them. If the currently
   * selected type's segments are empty, fetch the real value from the provider's identifiers via
   * the unmask API, split it into the segments the real PDF expects, and patch them into the form
   * before saving.
   */
  private ensureSelectedTaxIdSegmentsPopulated(onReady: () => void): void {
    if (!this.isW9Form()) {
      onReady();
      return;
    }

    const formGroup = this.formRenderer?.formGroup;
    const rawTinType = formGroup?.get(W9_FIELD_IDS.TAX_ID_TYPE)?.value;
    const providerId = this.resolveProviderId();

    if ((rawTinType !== TAX_ID_TYPE.SSN && rawTinType !== TAX_ID_TYPE.EIN) || !formGroup || !providerId) {
      onReady();
      return;
    }
    const tinType: TaxIdType = rawTinType;

    const segmentIds = ProviderEnrollmentFormsComponent.TIN_SEGMENT_FIELD_IDS[tinType];
    const alreadyPopulated = segmentIds.some(id => !!formGroup.get(id)?.value);
    if (alreadyPopulated) {
      onReady();
      return;
    }

    const cacheKey = `${providerId}:${tinType}`;
    const cached = this.unmaskedIdentifierCache.get(cacheKey);
    if (cached) {
      this.applyUnmaskedTaxId(formGroup, tinType, cached);
      onReady();
      return;
    }

    const payload = {
      programCode: this.programCode,
      identifier: String(providerId),
      source: W9_UNMASK_IDENTIFIER_CONTEXT.SOURCE,
      identifierType: tinType,
      userType: W9_UNMASK_IDENTIFIER_CONTEXT.USER_TYPE,
      userCategory: W9_UNMASK_IDENTIFIER_CONTEXT.USER_CATEGORY,
    };

    this.leadDetailsService.unmaskIdentifier(this.programCode, payload).subscribe({
      next: (response: any) => {
        const raw = String(response?.responsedata ?? '').replace(/\D/g, '');
        if (raw) {
          this.unmaskedIdentifierCache.set(cacheKey, raw);
          this.applyUnmaskedTaxId(formGroup, tinType, raw);
        }
        onReady();
      },
      error: () => onReady(),
    });
  }

  /** Split the real (unmasked) SSN/EIN digits into the segments the actual W-9 PDF's fields expect:
   *  SSN = first 3 / next 2 / next 4 digits; EIN = first 2 / next 7 digits. */
  private applyUnmaskedTaxId(formGroup: FormGroup, tinType: TaxIdType, raw: string): void {
    if (tinType === TAX_ID_TYPE.SSN) {
      formGroup.patchValue({
        [W9_FIELD_IDS.SSN_SEGMENT_1]: raw.slice(0, 3),
        [W9_FIELD_IDS.SSN_SEGMENT_2]: raw.slice(3, 5),
        [W9_FIELD_IDS.SSN_SEGMENT_3]: raw.slice(5, 9),
      });
    } else {
      formGroup.patchValue({
        [W9_FIELD_IDS.EIN_SEGMENT_1]: raw.slice(0, 2),
        [W9_FIELD_IDS.EIN_SEGMENT_2]: raw.slice(2, 9),
      });
    }
  }

  // updateDemographics returns its own (narrower — no identifiers/contactDetails) copy of
  // basicInfo, with a modifiedDate the backend has already advanced past what
  // getAssociationsDetails originally gave us. The next update call must echo *that* modifiedDate
  // back or the backend 406s with "Concurrent modification not allowed!". Kept separate from
  // associationDetails.basicInfo (untouched) since that copy is still needed elsewhere on this
  // form (SSN/EIN, Mailing address) and this narrower shape can't safely replace it.
  private latestDemographicsBasicInfo: any = null;

  /**
   * this.providerId/selectedProviderType aren't reliably populated in every render context
   * (e.g. came through as '' in one case, silently becoming id:0 via Number('')). basicInfo from
   * getAssociationsDetails() — the same source the rest of the W-9's prefill uses — always
   * carries the real numeric providerId, so prefer that.
   */
  private resolveProviderId(): number | undefined {
    const basicInfo = this.getDemographicsBasicInfo();
    return basicInfo?.providerId ?? basicInfo?.id ?? (this.providerId ? Number(this.providerId) : undefined);
  }

  /** basicInfo to build the next updateDemographics payload from — the last update response if
   *  we have one (its modifiedDate is what the backend now expects), else the original
   *  getAssociationsDetails copy. */
  private getDemographicsBasicInfo(): any {
    return this.latestDemographicsBasicInfo ?? this.associationDetails?.basicInfo;
  }

  /**
   * W-9: Legal Name of Business / DBA also live on the provider's demographics record. If the
   * user edited either on this form, push the change there too (in addition to the normal
   * questionnaire save below) — mirrors the update call in
   * lead-detail-participant-info.component.ts's save().
   */
  private updateLegalNameAndBusinessNameIfChanged(onReady: () => void): void {
    if (!this.isW9Form()) {
      onReady();
      return;
    }

    const formGroup = this.formRenderer?.formGroup;
    const nameControl = formGroup?.get(W9_FIELD_IDS.NAME);
    const businessNameControl = formGroup?.get(W9_FIELD_IDS.BUSINESS_NAME);
    if (!nameControl?.dirty && !businessNameControl?.dirty) {
      onReady();
      return;
    }

    const basicInfo = this.getDemographicsBasicInfo();
    const providerId = this.resolveProviderId();
    const providerType = basicInfo?.providerType ?? this.selectedProviderType;

    if (!providerId) {
      console.warn('Skipping Legal Name/DBA demographics update: provider id is unavailable.');
      onReady();
      return;
    }

    // Backend requires the full demographics record, not a partial patch (see the earlier "Id
    // cannot be null"/"Validation failed!" 400s) — spread basicInfo as-is (a fresh object; this
    // does not mutate this.associationDetails/basicInfo) and override only what this form itself
    // changes. modifiedDate is passed through unmodified — the backend requires the value it
    // last returned (see getDemographicsBasicInfo), not a freshly generated one. identifiers is
    // explicitly dropped: it's the masked SSN/EIN display value from getAssociationsDetails, not
    // the real value, and must not be echoed back into the record.
    const payload: Record<string, any> = {
      ...basicInfo,
      id: providerId,
      programCode: this.programCode,
      providerType,
      legalName: nameControl?.value ?? '',
      businessName: businessNameControl?.value ?? '',
    };
    delete payload['identifiers'];

    this.leadDetailsService.updateLeadDemographics(payload, STATIC_DATA.PROVIDER_ENROLLMENT).subscribe({
      // The backend advances modifiedDate on every successful update — remember its response so
      // the *next* update call (e.g. editing Legal Name again later in this session) sends the
      // current modifiedDate instead of the one we started with, avoiding a 406 "Concurrent
      // modification not allowed!".
      next: (response: any) => {
        if (response?.responsedata) {
          this.latestDemographicsBasicInfo = response.responsedata;
        }
        onReady();
      },
      error: () => onReady(),
    });
  }

  onFormSaved(event: { checklistItemId: number; checklistItemDetailId: number }): void {
    const isCurrentChecklist =
      this.currentChecklist?.checklistItemId === event.checklistItemId &&
      this.currentChecklist?.checklistItemDetailId === event.checklistItemDetailId;

    if (!isCurrentChecklist || !this.currentChecklist) {
      this.emitCompletionState();
      return;
    }

    this.currentChecklist.formStatus = this.currentChecklist?.formMasterId === 113 ? 'C' : 'I';
    this.setPendingResignState(this.currentChecklist, true);
    this.refreshChecklistGroupProgress();

    this.disableSave = true;
    this.disableActions = false;
    this.currentFormSaved = true;

    const currentCacheKey = this.getChecklistCacheKey(this.currentChecklist);
    const currentFormValue = this.formRenderer?.formGroup.getRawValue();
    if (currentCacheKey && currentFormValue) {
      this.prefillCache.set(currentCacheKey, currentFormValue);
    }

    // FLSA and Tax Exemptions saves can ADD or REMOVE dependent forms (background-check forms
    // for FLSA; CA DE 1378J for Tax Exemptions). The backend now drives that set, so ask the
    // parent to refetch the checklist. Local hide flags stay as defensive fallback.
    if (this.currentChecklist?.formMasterId === this.FLSA_FORM_ID ||
        this.currentChecklist?.formMasterId === this.TAX_EXEMPTIONS_FORM_ID) {
      // Stash the current form id so the post-refetch init can restore selection (the dynamic
      // checklistItemId/DetailId may change in the refreshed payload, but formMasterId is stable).
      this.pendingRestoreFormMasterId = this.currentChecklist.formMasterId;
      this.reapplyVisibilityForAll();
      this.actions.emit({ action: 'refetch' });
    }

    this.emitCompletionState();
  }

  onFormChanged(hasChanges: boolean): void {
    if (!this.hasQuestionnaireFields()) {
      this.disableSave = true;
      this.disableActions = false;
      return;
    }

    // A read-only questionnaire has every control disabled, which makes formGroup.valid false
    // (an all-disabled FormGroup reports status DISABLED). The validity-based rule below would
    // then set disableActions = true and hide Sign/View — so bail out and leave the action state
    // to updateButtonStates, which derives it from the saved answers instead. Nothing here is
    // editable, so Save stays off.
    if (this.isReadOnly) {
      this.disableSave = true;
      return;
    }

    if (hasChanges) {
      this.currentFormSaved = false;
    }

    const isFormValid = this.formRenderer?.formGroup.valid ?? false;
    this.disableSave = !hasChanges || !isFormValid;

    if (this.currentChecklist?.formStatus === 'C' && !hasChanges) {
      this.disableActions = false;
    } else {
      // Enable the PDF actions once every required question is answered (form valid) and there
      // are no unsaved changes — a valid form is, by definition, fully answered. This no longer
      // depends on the persisted formStatus flag, which stays 'P' until signing and would keep
      // the actions disabled for a fully-answered (but unsigned) form.
      this.disableActions = hasChanges || !isFormValid;
    }
  }

  // ───────────────────────────────────────────────
  //  Status helpers
  // ───────────────────────────────────────────────

  hasQuestionnaireFields(): boolean {
    return this.currentChecklist?.form?.sections.some(s => s.fields?.length > 0) ?? false;
  }

  /** IRS Form W-9 gets a reminder above Save Form to confirm the prefilled Mailing Address and
   *  SSN/EIN, and that corrections belong in the Enrollment Information tab, not on this form. */
  isW9Form(): boolean {
    return this.currentChecklist?.form?.name === W9_FORM_NAME;
  }

  // ───────────────────────────────────────────────
  //  Data loading (private)
  // ───────────────────────────────────────────────

  private loadForms(isupdate: boolean): void {
    this.loading = true;
    this.error = false;

    this.dynamicFormService.getForms(this.programCode).subscribe({
      next: (forms) => {
        if (this.checklistGroups && this.checklistGroups.length) {
          this.forms = [];
          this.initializeFormsFromChecklist(forms, this.checklistGroups, isupdate);
        } else {
          this.forms = [];
          this.currentChecklist = null;
        }

        this.formsLoaded.emit(this.forms.length);
        this.emitCompletionState();
        this.loading = false;
      },
      error: (err) => {
        this.error = true;
        this.errorMessage = 'Failed to load forms. Please refresh the page.';
        this.loading = false;
        console.error('Error loading forms:', err);
      },
    });
  }

  private initializeFormsFromChecklist(forms: DynamicForm[], checklistGroups: ChecklistGroup[], isupdate: boolean): void {

    // If a refetch was triggered (e.g. after Tax Exemptions save), remember and clear the pending
    // restore id so we can rebind currentChecklist by formMasterId after the per-group loop.
    const restoreFormMasterId = this.pendingRestoreFormMasterId;
    this.pendingRestoreFormMasterId = null;

    for (const checklistGroup of checklistGroups) {
      for (const checklistItem of checklistGroup.items) {
        this.applyPendingResignState(checklistItem);
        const form = forms.find(f => Number(f.id) === checklistItem.formMasterId);
        if (form) {
          checklistItem.form = form;
          this.forms.push(form);
        } else {
          console.warn(`No matching form found for checklist item with formMasterId ${checklistItem.formMasterId}`);
          continue;
        }

        this.applyChecklistItemVisibility(checklistItem, form);
      }

      // Eagerly load FLSA form data so background-check form visibility is correct on first render
      const flsaItem = checklistGroup.items.find(ci => ci.formMasterId === this.FLSA_FORM_ID);
      if (flsaItem?.form) {
        this.loadFormDataIntoCache(flsaItem);
      }

      // Eagerly load Tax Exemptions form data so visibility is correct on first render
      const taxExemptionsItem = checklistGroup.items.find(ci => ci.formMasterId === this.TAX_EXEMPTIONS_FORM_ID);
      if (taxExemptionsItem?.form) {
        this.loadFormDataIntoCache(taxExemptionsItem);
      }

      this.refreshChecklistGroupProgress();

      const firstVisibleItem = checklistGroup.items.find(item => !item.hide && item.formStatus !== 'C')
        ?? checklistGroup.items.find(item => !item.hide);

      // Only auto-select a visible item: a group whose forms are all hidden (e.g. Provider
      // Classification) must not open a hidden form in the main area.
      if (!isupdate && !this.currentChecklist && firstVisibleItem) {
        this.currentChecklist = firstVisibleItem;
        this.currentChecklistGroup = checklistGroup;
        this.currentFormSaved = false;
        this.loadFormData(this.currentChecklist);
      } else if (isupdate && this.currentChecklist && this.currentChecklistGroup?.checklistName === checklistGroup.checklistName) {
        // Refresh currentChecklist reference only within the same group to avoid matching
        // the same checklistItemId/checklistItemDetailId from a different group
        const refreshed = checklistGroup.items.find(
          (item) =>
            item.checklistItemId === this.currentChecklist!.checklistItemId &&
            item.checklistItemDetailId === this.currentChecklist!.checklistItemDetailId,
        );
        if (refreshed?.hide && firstVisibleItem) {
          // Current selection became hidden (e.g. SS-4 after EIN detected), switch to first visible
          this.currentChecklist = firstVisibleItem;
          this.currentChecklistGroup = checklistGroup;
          this.currentFormSaved = false;
          this.loadFormData(firstVisibleItem);
        } else if (refreshed) {
          this.currentChecklist = refreshed;
          this.currentChecklistGroup = checklistGroup;
          this.currentFormSaved = false;
          this.updateButtonStates(refreshed);
        }
      }
    }

    // Load the I-9 citizenship answer so the Tax Exemptions non-resident-alien option can be
    // greyed out for US citizens (Scenarios 3 & 4). Done once all groups are built.
    this.loadI9CitizenshipStatus();

    // Post-refetch: restore the selection that was active before the refresh, matching on the
    // stable formMasterId. Wins over the per-group rebind above when set, because after a refetch
    // the dynamic checklistItemId / checklistItemDetailId can change for the same logical form.
    if (restoreFormMasterId != null) {
      for (const group of checklistGroups) {
        const target = group.items.find(item => item.formMasterId === restoreFormMasterId);
        if (target) {
          this.currentChecklist = target;
          this.currentChecklistGroup = group;
          this.currentFormSaved = false;
          this.loadFormData(target);
          break;
        }
      }
    }
  }

  private applyChecklistItemVisibility(checklistItem: ChecklistItem, form: DynamicForm): void {
    if (Number(form.id) === IC_PROVIDER_CLASSIFICATION_FORM.formMasterId || Number(form.id) === VE_PROVIDER_CLASSIFICATION_FORM.formMasterId) {
      // Provider Classification is answered through its own questionnaire
      // (ppl-provider-classification), so it is never shown as a form here.
      checklistItem.hide = true;
    } else {
      checklistItem.hide = false;
    }
  }

  private getFlsaFormData(): Record<string, any> | undefined {
    const flsaItem = this.getAllChecklistItems().find(ci => ci.formMasterId === this.FLSA_FORM_ID);
    if (!flsaItem) return undefined;
    return this.prefillCache.get(this.getChecklistCacheKey(flsaItem));
  }

  private getTaxExemptionsFormData(): Record<string, any> | undefined {
    const taxItem = this.getAllChecklistItems().find(ci => ci.formMasterId === this.TAX_EXEMPTIONS_FORM_ID);
    if (!taxItem) return undefined;
    return this.prefillCache.get(this.getChecklistCacheKey(taxItem));
  }

  /** True when the provider's I-9 form is fully completed (Scenarios 1 & 2). */
  private isI9Complete(): boolean {
    return this.getAllChecklistItems().some(
      ci => ci.formMasterId === this.I9_FORM_ID && ci.formStatus === 'C');
  }

  /** True when the I-9 citizenship answer indicates a US citizen. */
  private isI9UsCitizen(): boolean {
    return this.i9CitizenshipStatus === this.I9_US_CITIZEN_VALUE;
  }

  /**
   * Read the I-9's saved citizenship answer. The I-9 is a PDF form with no dynamic-form fields, so
   * its answer isn't captured by buildPrefillData — read it straight from the raw Form_Fields.
   */
  private loadI9CitizenshipStatus(): void {
    const i9Item = this.getAllChecklistItems().find(ci => ci.formMasterId === this.I9_FORM_ID);
    if (!i9Item?.form) return;

    this.checkListService
      .getFormData(this.programCode, i9Item, this.source, this.participantPPLID, '', this.isAssociation, this.formEntitiesOverride)
      .subscribe({
        next: (response) => {
          const fields = response?.responsedata?.Form_Fields;
          if (!Array.isArray(fields)) return;
          const citizenship = fields.find((f: any) =>
            String(f?.fieldId ?? '').trim() === this.I9_CITIZENSHIP_FIELD ||
            String(f?.businessName ?? '').trim() === this.I9_CITIZENSHIP_FIELD);
          this.i9CitizenshipStatus = citizenship ? this.getValueFromPdfField(citizenship) : undefined;
          // Route through reapplyVisibilityForAll so the option grey-out is re-evaluated even if the
          // Tax Exemptions data cached before this I-9 fetch resolved.
          this.reapplyVisibilityForAll();
        },
      });
  }

  /** Locate the non-resident-alien visa option on the Tax Exemptions form config. */
  private findNonResidentAlienOption(): FieldOption | undefined {
    const taxForm = this.forms.find(f => Number(f.id) === this.TAX_EXEMPTIONS_FORM_ID);
    if (!taxForm) return undefined;
    for (const section of taxForm.sections) {
      const field = section.fields.find(fld => fld.id === this.TAX_ADDITIONAL_INFO_FIELD);
      const option = field?.options?.find(o => o.value === this.NON_RESIDENT_ALIEN_VALUE);
      if (option) return option;
    }
    return undefined;
  }

  /** True when the non-resident-alien option is selected in the saved Tax Exemptions data. */
  private isNonResidentAlienSelected(): boolean {
    const selected = this.getTaxExemptionsFormData()?.[this.TAX_ADDITIONAL_INFO_FIELD];
    return Array.isArray(selected)
      ? selected.includes(this.NON_RESIDENT_ALIEN_VALUE)
      : selected === this.NON_RESIDENT_ALIEN_VALUE;
  }

  /**
   * Grey out the non-resident-alien visa option for US citizens (Scenarios 3 & 4). If it is already
   * selected, keep it enabled so it can still be deselected.
   */
  private applyTaxExemptionFieldRules(): void {
    const option = this.findNonResidentAlienOption();
    if (!option) return;
    option.disabled = this.isI9UsCitizen() && !this.isNonResidentAlienSelected();
  }

  /** Re-evaluate hide flag for every checklist item (call after FLSA form data changes). */
  private reapplyVisibilityForAll(): void {
    for (const checklistGroup of this.checklistGroups ?? []) {
      for (const checklistItem of checklistGroup.items) {
        const form = this.forms.find(f => Number(f.id) === checklistItem.formMasterId);
        if (form) {
          this.applyChecklistItemVisibility(checklistItem, form);
        }
      }
    }
    this.refreshChecklistGroupProgress();
    // Re-evaluate the non-resident-alien option grey-out whenever cached form data changes, so it is
    // correct regardless of the order in which the I-9 and Tax Exemptions data load (Scenarios 3 & 4).
    this.applyTaxExemptionFieldRules();
  }

  /** Silently fetch a form's saved data into prefillCache without affecting the current view. */
  private loadFormDataIntoCache(checklistItem: ChecklistItem): void {
    const cacheKey = this.getChecklistCacheKey(checklistItem);
    if (this.prefillCache.has(cacheKey) || !checklistItem.form) return;

    this.checkListService
      .getFormData(this.programCode, checklistItem, this.source, this.participantPPLID, '', this.isAssociation, this.formEntitiesOverride)
      .subscribe({
        next: (response) => {
          if (response?.responsedata && checklistItem.form && Array.isArray(response.responsedata.Form_Fields)) {
            const prefill = this.buildPrefillData(checklistItem.form, response.responsedata.Form_Fields, checklistItem);
            this.prefillCache.set(cacheKey, prefill);
            this.reapplyVisibilityForAll();
          }
        },
      });
  }

  private loadFormData(checklistItem: ChecklistItem | null): void {
    if (!checklistItem?.form) return;

    this.updateButtonStates(checklistItem);
    this.loadPDFData(checklistItem, checklistItem.form);
  }

  loadPDFData(checklistItem: ChecklistItem, dynamicForm?: DynamicForm): void {
    this.loading = true;
    const targetCacheKey = this.getChecklistCacheKey(checklistItem);

    this.checkListService
      .getFormData(this.programCode, checklistItem, this.source, this.participantPPLID, '', this.isAssociation, this.formEntitiesOverride)
      .subscribe({
        next: (response) => {
          if (response?.responsedata) {
            this.pdfConfig = {
              fields: response.responsedata.Form_Fields,
              form_description: checklistItem.formDesc,
              form_id: checklistItem.formMasterId + '',
              form_name: checklistItem.form_name,
              is_mandatory: false,
            };

            if (dynamicForm && Array.isArray(response.responsedata.Form_Fields)) {
              const prefill = this.buildPrefillData(dynamicForm, response.responsedata.Form_Fields, checklistItem);
              if (targetCacheKey) {
                this.prefillCache.set(targetCacheKey, prefill);
              }
              // Recompute button states now that the saved values are cached, so a fully-answered
              // (but unsigned) form re-opened from the server doesn't keep the PDF actions disabled.
              this.updateButtonStates(checklistItem);
              if (this.getChecklistCacheKey(this.currentChecklist) === targetCacheKey) {
                queueMicrotask(() => this.formRenderer?.applyPrefillData(prefill));
              }
              if (checklistItem.formMasterId === this.FLSA_FORM_ID ||
                  checklistItem.formMasterId === this.TAX_EXEMPTIONS_FORM_ID) {
                this.reapplyVisibilityForAll();
              }
            }
          }

          this.loading = false;
        },
        error: (err) => {
          this.error = true;
          this.errorMessage = 'Failed to load form data. Please refresh the page.';
          this.loading = false;
          console.error('Error loading form data:', err);
        },
      });
  }

  // ───────────────────────────────────────────────
  //  Prefill: PDF → form value transformation
  // ───────────────────────────────────────────────

  private buildPrefillData(dynamicForm: DynamicForm, pdfFields: any[], checklistItem: ChecklistItem): Record<string, any> {
    const prefill: Record<string, any> = {};
    const fieldLookup = this.buildPdfFieldLookup(pdfFields);
    let hasCompletedRequiredFields = dynamicForm.sections.length !== 0;

    for (const section of dynamicForm.sections) {
      for (const formField of section.fields) {
        const value = formField.type === 'checkbox' && Array.isArray(formField.options)
          ? this.resolveCheckboxValue(formField, fieldLookup)
          : this.resolveStandardFieldValue(formField, fieldLookup);

        if (value !== undefined) {
          prefill[formField.id] = value;
        }

        if (formField.required && (value === null || value === undefined || value === '' || (Array.isArray(value) && value.length === 0))) {
          hasCompletedRequiredFields = false;
        }
      }
    }

    // If a combined Height value (e.g. "5'7"") was saved, back-fill the two sub-fields
    // so the dropdowns are pre-selected when the form loads.
    const savedHeight = prefill['Height'];
    if (savedHeight && typeof savedHeight === 'string') {
      const heightMatch = savedHeight.match(/^(\d+)'(\d+)/);
      if (heightMatch) {
        prefill['Height Feet'] = heightMatch[1];
        prefill['Height Inches'] = heightMatch[2];
      }
    }

    this.applyAssociationDetailsOverrides(dynamicForm, prefill);

    if (hasCompletedRequiredFields && checklistItem && checklistItem.formStatus !== 'C') {
      checklistItem.formStatus = checklistItem?.formMasterId === 113 ? 'C' : 'I';
    }

    return prefill;
  }

  /**
   * W-9: overlay Legal Name of Business, DBA, Mailing Address and SSN/EIN identifier values from
   * the association's basicInfo/contactDetails (EnrollmentDataService.getAssociationsDetails)
   * onto the PDF-derived prefill — only for whichever of these fields the current form defines.
   */
  private applyAssociationDetailsOverrides(dynamicForm: DynamicForm, prefill: Record<string, any>): void {
    const basicInfo = this.associationDetails?.basicInfo;
    const contactDetails = this.associationDetails?.contactDetails;
    if (!basicInfo && !contactDetails) return;

    const hasField = (id: string) =>
      dynamicForm.sections.some(section => section.fields.some(f => f.id === id));

    if (hasField(W9_FIELD_IDS.NAME) && basicInfo?.legalName) {
      prefill[W9_FIELD_IDS.NAME] = basicInfo.legalName;
    }

    if (hasField(W9_FIELD_IDS.BUSINESS_NAME) && basicInfo?.businessName) {
      prefill[W9_FIELD_IDS.BUSINESS_NAME] = basicInfo.businessName;
    }

    if (hasField(W9_FIELD_IDS.MAILING_ADDRESS)) {
      const mailingAddress = this.composeMailingAddress(contactDetails);
      if (mailingAddress) {
        prefill[W9_FIELD_IDS.MAILING_ADDRESS] = mailingAddress;
      }
    }

    const identifiers: any[] = Array.isArray(basicInfo?.identifiers) ? basicInfo.identifiers : [];
    const ssn = identifiers.find(i => i?.type === TAX_ID_TYPE.SSN && i?.isActive);
    const ein = identifiers.find(i => i?.type === TAX_ID_TYPE.EIN && i?.isActive);

    // Fed into the "Social Security Number"/"Employer Identification Number" display fields via
    // stringAutoCalculate, so the value survives being hidden-and-nulled when the other Taxpayer
    // ID type is selected, and reappears correctly if the user toggles back.
    if (hasField(W9_FIELD_IDS.SSN_VALUE) && ssn?.value) {
      prefill[W9_FIELD_IDS.SSN_VALUE] = ssn.value;
    }
    if (hasField(W9_FIELD_IDS.EIN_VALUE) && ein?.value) {
      prefill[W9_FIELD_IDS.EIN_VALUE] = ein.value;
    }
    // Taxpayer Identification Number Type is deliberately left unset here — the user must
    // explicitly pick SSN or EIN themselves; it should never come pre-selected.
  }

  /** Combine street/city/state/zip from contactDetails into one display string (prefers mailingAddress, falls back to permanentAddress). */
  private composeMailingAddress(contactDetails: any): string {
    const address = contactDetails?.mailingAddress ?? contactDetails?.permanentAddress;
    if (!address) return '';

    const isNonBlank = (part: unknown): part is string => typeof part === 'string' && part.trim().length > 0;

    const line1 = [address.street1, address.street2].filter(isNonBlank).join(' ');
    const cityStateZip = [address.city, [address.state, address.zipCode].filter(isNonBlank).join(' ')]
      .filter(isNonBlank)
      .join(', ');

    return [line1, cityStateZip].filter(isNonBlank).join(', ');
  }

  /** Resolve prefill value for a checkbox field by checking each option against PDF data. */
  private resolveCheckboxValue(
    formField: DynamicFormField,
    fieldLookup: Map<string, any>,
  ): any | undefined {
    const options = formField.options!;
    const selectedValues: any[] = [];

    for (const option of options) {
      const optionKey = option.key ?? option.label;
      const match = this.findInLookup(fieldLookup, optionKey);

      if (match) {
        const pdfValue = this.getValueFromPdfField(match);
        if (pdfValue === 'Yes' || pdfValue === true || pdfValue === option.value) {
          selectedValues.push(option.value);
        }
      }
    }

    if (selectedValues.length > 0) {
      return options.length > 1 ? selectedValues : selectedValues[0];
    }

    // Fallback: try to match directly by field.id
    return this.resolveStandardFieldValue(formField, fieldLookup);
  }

  /** Resolve prefill value for a non-checkbox field by looking up its id in PDF data. */
  private resolveStandardFieldValue(
    formField: DynamicFormField,
    fieldLookup: Map<string, any>,
  ): any | undefined {
    const match = this.findInLookup(fieldLookup, formField.id);
    if (!match) return undefined;

    const value = this.getValueFromPdfField(match);
    if (value === undefined) return undefined;

    return this.normalizeValueForFormField(formField, value);
  }

  /** Extract the effective value from a PDF field, considering childFields. */
  private getValueFromPdfField(pdfField: any): any {
    const businessName = typeof pdfField?.businessName === 'string' ? pdfField.businessName.trim() : '';
    const childFields: any[] = Array.isArray(pdfField?.childFields) ? pdfField.childFields : [];

    if (childFields.length) {
      const relevant = businessName
        ? childFields.filter(cf => String(cf?.fieldId ?? '').trim() === businessName)
        : childFields;

      const candidates = (relevant.length ? relevant : childFields)
        .map(cf => cf?.value)
        .filter(v => v !== undefined && v !== null && v !== '');

      if (candidates.length === 1) return candidates[0];
      if (candidates.length > 1) return Array.from(new Set(candidates));
    }

    return pdfField?.value;
  }

  /** PDF sentinel values that represent "no selection" (e.g. unchecked radio buttons). */
  private static readonly PDF_EMPTY_SENTINELS = new Set(['Off', 'off', 'OFF']);

  /** Normalize a raw value to match the form control's expected shape. */
  private normalizeValueForFormField(formField: any, value: any): any {
    if (value === null || value === undefined) return value;

    // Radio/dropdown: reject PDF sentinels and values not in the options list.
    if ((formField?.type === 'radio' || formField?.type === 'dropdown') && Array.isArray(formField?.options)) {
      if (ProviderEnrollmentFormsComponent.PDF_EMPTY_SENTINELS.has(value)) return null;
      const validValues = new Set(formField.options.map((opt: any) => opt.value));
      if (!validValues.has(value)) return null;
    }

    if (formField?.type === 'checkbox' && Array.isArray(formField?.options)) {
      const validValues = new Set(formField.options.map((opt: any) => opt.value));

      if (formField.options.length > 1) {
        const normalizedValues = Array.isArray(value)
          ? value
          : (ProviderEnrollmentFormsComponent.PDF_EMPTY_SENTINELS.has(value) ? [] : [value]);

        return normalizedValues.filter((item: any) => validValues.has(item));
      }

      if (ProviderEnrollmentFormsComponent.PDF_EMPTY_SENTINELS.has(value)) return null;
      return validValues.has(value) ? value : null;
    }

    return value;
  }

  // ───────────────────────────────────────────────
  //  Utility (private)
  // ───────────────────────────────────────────────

  /** Build a case-preserving lookup Map keyed by businessName. */
  private buildPdfFieldLookup(pdfFields: any[]): Map<string, any> {
    const lookup = new Map<string, any>();
    for (const field of pdfFields) {
      const key = typeof field?.businessName === 'string' ? field.businessName.trim() : '';
      if (key) {
        lookup.set(key, field);
      }
    }
    return lookup;
  }

  /** Find a PDF field by name with exact match first, then case-insensitive fallback. */
  private findInLookup(lookup: Map<string, any>, name: string): any | undefined {
    return (
      lookup.get(name) ??
      lookup.get(name.trim()) ??
      Array.from(lookup.entries()).find(([k]) => k.toLowerCase() === name.toLowerCase())?.[1]
    );
  }

  /** Flatten checklistGroups / checklist into a single array. */
  private getAllChecklistItems(): ChecklistItem[] {
    if (this.checklistGroups?.length) {
      return this.checklistGroups.flatMap(group => group.items);
    }
    return [];
  }

  /** Apply cached prefill data to the renderer if both are available. */
  private applyPrefillIfCached(): void {
    const cached = this.currentChecklist
      ? this.prefillCache.get(this.getChecklistCacheKey(this.currentChecklist))
      : undefined;

    if (this._formRenderer && cached) {
      queueMicrotask(() => this._formRenderer?.applyPrefillData(cached));
    }
  }

  /** Update save/action button states based on the form's questionnaire presence and completion status. */
  private updateButtonStates(checklistItem: ChecklistItem): void {
    const form = checklistItem.form;
    if (!form) return;

    const hasFields = form.sections.some(s => s.fields?.length > 0);

    if (!hasFields) {
      this.disableSave = true;
      this.disableActions = false;
      return;
    }

    // Enable the PDF actions once the questionnaire is actually complete — either the form is
    // already signed ('C') or every required question has an answer in the saved data. We no
    // longer gate on the persisted formStatus 'P' flag, which only flips on signing and would
    // otherwise keep the actions disabled for a fully-answered (but unsigned) form re-opened
    // from the server.
    // For the form currently on screen, trust the renderer's own state: it knows which required
    // fields conditional logic has hidden (those are disabled and blanked, so they never appear in
    // the saved values). Scanning the saved values for those would report the form as unanswered
    // and hide the PDF actions on a form the user has already saved.
    const liveRenderer = this.getRendererFor(checklistItem);
    const savedValues = this.prefillCache.get(this.getChecklistCacheKey(checklistItem)) ?? {};
    const allRequiredAnswered =
      checklistItem.formStatus === 'C' ||
      (liveRenderer
        ? liveRenderer.formGroup.valid && !liveRenderer.formGroup.dirty
        : this.areAllRequiredFieldsAnswered(form, savedValues));

    this.disableSave = true;
    this.disableActions = !allRequiredAnswered;
  }

  /**
   * The rendered form for `checklistItem`, or undefined when the renderer is still showing a
   * different form (e.g. mid-navigation, before change detection has rebound its inputs).
   */
  private getRendererFor(checklistItem: ChecklistItem): DynamicFormRendererComponent | undefined {
    const renderer = this.formRenderer;
    if (!renderer?.formGroup || renderer.formConfig !== checklistItem.form) return undefined;

    const rendered = renderer.checklistItem;
    const isSameItem =
      rendered?.checklistItemId === checklistItem.checklistItemId &&
      rendered?.checklistItemDetailId === checklistItem.checklistItemDetailId;

    return isSameItem ? renderer : undefined;
  }

  /** True when every required field in the form has a non-empty value in the supplied data map. */
  private areAllRequiredFieldsAnswered(form: DynamicForm, values: Record<string, any>): boolean {
    if (!form.sections.length) return false;

    for (const section of form.sections) {
      for (const field of section.fields) {
        if (!field.required) continue;

        const value = values[field.id];
        if (value === null || value === undefined || value === '' || (Array.isArray(value) && value.length === 0)) {
          return false;
        }
      }
    }

    return true;
  }

  private isCurrentFormCompleted(): boolean {
    return this.currentChecklist?.formStatus === 'C';
  }

  private getChecklistCacheKey(checklistItem: ChecklistItem | null | undefined): string {
    return checklistItem
      ? `${checklistItem.checklistItemId}:${checklistItem.checklistItemDetailId}`
      : '';
  }

  private getPendingResignStorageKey(checklistItem: ChecklistItem | null | undefined): string {
    const checklistKey = this.getChecklistCacheKey(checklistItem);
    return checklistKey ? `provider-form-pending-resign:${checklistKey}` : '';
  }

  private setPendingResignState(checklistItem: ChecklistItem | null | undefined, isPending: boolean): void {
    const storageKey = this.getPendingResignStorageKey(checklistItem);
    if (!storageKey) {
      return;
    }

    if (isPending) {
      sessionStorage.setItem(storageKey, 'true');
      return;
    }

    sessionStorage.removeItem(storageKey);
  }

  private applyPendingResignState(checklistItem: ChecklistItem | null | undefined): void {
    const storageKey = this.getPendingResignStorageKey(checklistItem);
    if (!storageKey) {
      return;
    }

    if (sessionStorage.getItem(storageKey) === 'true') {
      checklistItem!.formStatus = (checklistItem?.formStatus === 'C' || checklistItem?.formMasterId === 113) ? 'C' : 'I';
    }
  }

  private refreshChecklistGroupProgress(): void {
    for (const group of this.checklistGroups ?? []) {
      const visibleItems = group.items.filter((item) => !item.hide && !!item.form);
      group.totalItems = visibleItems.length;
      group.totalItemsCompleted = visibleItems.filter((item) => item.formStatus === 'C').length;
      group.isCompleted = group.totalItems > 0 && group.totalItemsCompleted === group.totalItems;
    }
  }

  private emitCompletionState(): void {
    const checklistItems = this.getAllChecklistItems().filter((item) => !item.hide && !!item.form);
    const areAllCompleted = checklistItems.length > 0 && checklistItems.every((item) => item.formStatus === 'C');
    this.allFormsCompleted.emit(areAllCompleted);
  }

  private shouldReloadForms(changes: SimpleChanges): boolean {
    return [
      'providerId',
      'selectedProviderType',
      'selectedParticipantId',
      'participantPPLID',
      'programCode',
      'checklistGroups',
      'isAssociation',
      'relationToParticipant',
    ].some((key) => !!changes[key] && !changes[key].firstChange);
  }
}
